import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// ── Confirm Assignment (Case A: subproject has no site yet) ──────────────────

interface ConfirmAssignDialogProps {
  open: boolean;
  siteName: string;
  subprojectName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmAssignDialog({
  open,
  siteName,
  subprojectName,
  onConfirm,
  onCancel,
}: ConfirmAssignDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Assign site?</DialogTitle>
      <DialogContent>
        <Typography>
          Assign <strong>{siteName}</strong> to <strong>{subprojectName}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onConfirm} variant="contained" color="info">
          Assign
        </Button>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Conflict Assignment (Case B: subproject already has a different site) ────

interface ConflictAssignDialogProps {
  open: boolean;
  subprojectName: string;
  currentSiteName: string;
  newSiteName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConflictAssignDialog({
  open,
  subprojectName,
  currentSiteName,
  newSiteName,
  onConfirm,
  onCancel,
}: ConflictAssignDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Replace site assignment?</DialogTitle>
      <DialogContent>
        <Typography>
          <strong>{subprojectName}</strong> is currently assigned to{' '}
          <strong>{currentSiteName}</strong>. Replace it with{' '}
          <strong>{newSiteName}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onConfirm} variant="contained" color="info">
          Replace
        </Button>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete Site Warning (panel list delete button) ───────────────────────────

interface DeleteSiteDialogProps {
  open: boolean;
  siteName: string;
  subprojectNames: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeleteSiteDialog({
  open,
  siteName,
  subprojectNames,
  onConfirm,
  onCancel,
}: DeleteSiteDialogProps) {
  const hasSubprojects = subprojectNames.length > 0;
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Delete site?</DialogTitle>
      <DialogContent>
        {hasSubprojects ? (
          <Typography>
            <strong>{siteName}</strong> is assigned to{' '}
            <strong>{subprojectNames.join(', ')}</strong>. Deleting it will
            remove {subprojectNames.length === 1 ? 'the' : 'their'} site
            assignment{subprojectNames.length === 1 ? '' : 's'}. This cannot be
            undone.
          </Typography>
        ) : (
          <Typography>
            Delete <strong>{siteName}</strong>? This cannot be undone.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onConfirm} variant="contained" color="error">
          Delete site
        </Button>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Remove Assignment Warning (chip × on assigned subproject) ────────────────

interface RemoveAssignmentDialogProps {
  open: boolean;
  subprojectName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RemoveAssignmentDialog({
  open,
  subprojectName,
  onConfirm,
  onCancel,
}: RemoveAssignmentDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Remove site assignment?</DialogTitle>
      <DialogContent>
        <Typography>
          <strong>{subprojectName}</strong> will no longer have a site assigned.
          You can reassign it later.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onConfirm} variant="contained" color="info">
          Remove
        </Button>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Map Delete Confirmation (in-map delete tool Save button) ─────────────────

interface MapDeleteConfirmDialogProps {
  open: boolean;
  names: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function MapDeleteConfirmDialog({
  open,
  names,
  onConfirm,
  onCancel,
}: MapDeleteConfirmDialogProps) {
  const count = names.length;
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{count === 1 ? 'Delete site?' : `Delete ${count} sites?`}</DialogTitle>
      <DialogContent>
        {count === 1 ? (
          <Typography>
            Delete <strong>{names[0]}</strong>? Attached subprojects will be
            detached. This cannot be undone.
          </Typography>
        ) : (
          <>
            <Typography sx={{ mb: 1 }}>
              Delete the following sites? Attached subprojects will be detached.
              This cannot be undone.
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {names.map((n, i) => (
                <li key={i}>
                  <Typography variant="body2">
                    <strong>{n}</strong>
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onConfirm} variant="contained" color="error">
          Delete
        </Button>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
